import {
  Component,
  EventEmitter,
  Input,
  OnDestroy,
  OnInit,
  Output,
} from '@angular/core';
import { FormBuilder, FormControl, FormGroup } from '@angular/forms';
import {
  map,
  Observable,
  distinctUntilChanged,
  Subject,
  takeUntil,
  switchMap,
  Subscription,
  merge,
  startWith,
  subscribeOn,
  asapScheduler,
  of,
  zip,
  mergeMap,
} from 'rxjs';
import {
  MapFilterFieldHandleType,
  MapFilterItem,
  MapFilterType,
} from 'app/shared/components/main-map/components/search-results/map-filter-state.model';
import { MapDomainService } from 'app/shared/services/map-domain.service';
import { CodedValueItem } from 'app/shared/interfaces/coded-value-item.interface';
import { MapFilterManager } from '../../map-filter.manager';

interface LocalizationForm {
  distrito: FormControl<CodedValueItem<string>>;
  concelho: FormControl<CodedValueItem<string>>;
  freguesia: FormControl<CodedValueItem<string>>;
  arruamento: FormControl<string>;
  npolicia: FormControl<string>;
}

@Component({
  selector: 'app-localization-filter-form',
  templateUrl: 'localization-filter-form.component.html',
  styleUrls: ['localization-filter-form.component.scss'],
})
export class LocalizationFilterFormComponent implements OnInit, OnDestroy {
  @Input()
  visible: boolean;
  @Output()
  visibleChange = new EventEmitter<boolean>();

  @Input()
  fields: __esri.Field[] = [];

  formGroup: FormGroup<LocalizationForm>;

  districtList: CodedValueItem<string>[];
  countyList: CodedValueItem<string>[];
  parishList: CodedValueItem<string>[];

  private filterList: MapFilterItem[] = [];
  private newFiltersMap = new Map<string, MapFilterItem>();
  private filterKeySetRemoved = new Set<string>();

  private filtersSubscription: Subscription;

  private handleValuesSubscription: Subscription;

  private lifecycle = new Subject<void>();

  constructor(
    private formBuilder: FormBuilder,
    private mapDomainService: MapDomainService,
    private mapFilterManager: MapFilterManager
  ) {}

  ngOnInit(): void {
    this.formGroup = this.formBuilder.group({
      distrito: this.formBuilder.control<CodedValueItem<string>>(null),
      concelho: this.formBuilder.control<CodedValueItem<string>>(null),
      freguesia: this.formBuilder.control<CodedValueItem<string>>(null),
      arruamento: this.formBuilder.control<string>(null),
      npolicia: this.formBuilder.control<string>(null),
    });

    this.mapDomainService
      .getDistrictDomainList()
      .pipe(takeUntil(this.lifecycle))
      .subscribe((list) => {
        this.districtList = list;
      });

    const observeLocationValueChanges = (
      fieldName: keyof LocalizationForm,
      callback: (value: any) => Subscription
    ) => {
      return this.observeFormValueChanges(fieldName)
        .pipe(
          switchMap((value) => {
            return new Observable((subscriber) => {
              subscriber.add(
                callback((value as CodedValueItem<string>)?.value)
              );
            });
          }),
          takeUntil(this.lifecycle)
        )
        .subscribe();
    };

    observeLocationValueChanges('distrito', (value) => {
      return this.loadCountyList(value);
    });

    observeLocationValueChanges('concelho', (value) => {
      return this.loadParishList(value);
    });

    merge(
      this.observeFormValueRangeChanges(['arruamento', 'npolicia']),
      this.observeFormValueRangeChanges([
        'distrito',
        'concelho',
        'freguesia',
      ]).pipe(
        map(({ fieldName, value }) => {
          let label: string;
          if (typeof value !== 'string') {
            label = value?.label;
            value = value?.label?.toLowerCase();
          }

          return {
            fieldName,
            value,
            label,
            fieldHandleType: MapFilterFieldHandleType.Lower,
          };
        })
      )
    )
      .pipe(takeUntil(this.lifecycle))
      .subscribe((evt) => {
        const { fieldName, value } = evt;

        if (value === null || value === undefined || value === '') {
          this.newFiltersMap.delete(fieldName);
          this.filterKeySetRemoved.add(fieldName);
        } else {
          const type = MapFilterType.Localization;
          let item = this.mapFilterManager.getFilterByTypeAndField(
            type,
            fieldName
          );
          if (!item) {
            item = new MapFilterItem({ type, field: fieldName });
          }

          const valueStr = MapFilterItem.stringfyValue(value);
          item.text = (evt as any).label ?? valueStr;
          item.fieldHandleType =
            (evt as any).fieldHandleType ?? MapFilterFieldHandleType.None;
          item.valueAsStr = valueStr;
          item.valueType = MapFilterItem.getValueType(value);

          this.newFiltersMap.set(fieldName, item);
          this.filterKeySetRemoved.delete(fieldName);
        }
      });
  }

  ngOnDestroy(): void {
    this.lifecycle.next();
    this.lifecycle.complete();
  }

  onShowingInternal() {
    this.filtersSubscription?.unsubscribe();
    this.filtersSubscription = this.mapFilterManager.onFiltersChanged
      .pipe(subscribeOn(asapScheduler), takeUntil(this.lifecycle))
      .subscribe((list) => {
        this.filterList = list.filter(
          (x) => x.type === MapFilterType.Localization
        );

        this.handleValues();
      });
  }

  onHiddenInternal() {
    this.filtersSubscription?.unsubscribe();
    this.handleValues();
  }

  onSubmit(e) {
    e?.preventDefault();

    const type = MapFilterType.Localization;
    const addsOrUpdates = Array.from(this.newFiltersMap, ([field, item]) => {
      const key = this.mapFilterManager.getFilterKeyBy({ type, field });
      if (key) {
        item.id = key;
      }

      return item;
    });
    const deletes = Array.from(this.filterKeySetRemoved.values(), (field) => {
      return this.mapFilterManager.getFilterKeyBy({ type, field });
    }).filter((x) => !!x);

    this.mapFilterManager.applyEditsFilters({
      adds: addsOrUpdates,
      updates: addsOrUpdates,
      deletes,
    });

    this.visible = false;
    this.visibleChange.emit(this.visible);
  }

  onBtnClearClick() {
    this.resetValues();
  }

  private handleValues() {
    this.handleValuesSubscription?.unsubscribe();

    this.filterKeySetRemoved.clear();
    this.newFiltersMap.clear();

    const values = this.filterList.reduce((acc, x) => {
      acc[x.field] = x.value;
      return acc;
    }, {});

    const observableList: Observable<any>[] = [of(null)];

    const auxList: Array<
      [string, (x: string) => Observable<CodedValueItem<string>>]
    > = [
      ['distrito', (x) => this.mapDomainService.getDistrictValueByLabel(x)],
      [
        'concelho',
        (x) =>
          this.mapDomainService.getCountyValueByLabel(
            values['distrito']?.value,
            x
          ),
      ],
      [
        'freguesia',
        (x) =>
          this.mapDomainService.getParishValueByLabel(
            values['concelho']?.value,
            x
          ),
      ],
    ];

    const handleLocationValues = (start = 0) => {
      for (let i = start; i < auxList.length; ++i) {
        const [fieldName, fn] = auxList[i];
        if (fieldName in values) {
          return fn(values[fieldName]).pipe(
            mergeMap((item) => {
              values[fieldName] = item;

              const observable = handleLocationValues(i + 1);
              if (!observable) {
                return of(null);
              }

              return observable;
            })
          );
        }
      }
    };

    const observable = handleLocationValues();
    if (observable) {
      observableList.push(observable);
    }

    this.handleValuesSubscription = zip(observableList)
      .pipe(takeUntil(this.lifecycle))
      .subscribe(() => {
        this.formGroup.reset(values);
      });
  }

  private resetValues() {
    this.handleValuesSubscription?.unsubscribe();
    this.filterKeySetRemoved.clear();
    this.newFiltersMap.clear();
    this.formGroup.reset();
  }

  private observeFormValueRangeChanges(
    fieldNames: Array<keyof LocalizationForm>
  ) {
    return merge(
      ...fieldNames.map((fieldName) => {
        return this.observeFormValueChanges(fieldName).pipe(
          map((value) => {
            return { fieldName, value };
          })
        );
      })
    );
  }

  private observeFormValueChanges(fieldName: keyof LocalizationForm) {
    const control = this.formGroup.get(fieldName);
    const initialValue = control.value;
    return control.valueChanges.pipe(
      startWith(initialValue),
      distinctUntilChanged()
    );
  }

  private loadCountyList(district: string) {
    return this.mapDomainService
      .getCountyDomainList(district)
      .subscribe((list) => {
        this.countyList = list;

        const control = this.formGroup.controls.concelho;
        if (list?.length) {
          control.enable();
        } else {
          control.disable();
        }
      });
  }

  private loadParishList(county: string) {
    return this.mapDomainService
      .getParishDomainList(county)
      .subscribe((list) => {
        this.parishList = list;

        const control = this.formGroup.controls.freguesia;
        if (list?.length) {
          control.enable();
        } else {
          control.disable();
        }
      });
  }
}
